import { Popover, Transition } from '@headlessui/react';
import { Upload } from 'antd';
import { TWButton, TWModal } from '../ui';
import React, { Fragment, useState } from 'react';
import ReactInputMask from 'react-input-mask';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import companyApi from '../../services/companyApi';
import { loginFailure } from '../../store/slices/authSlice';
import { setCompany, setGlobalCompany } from '../../store/slices/companySlice';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import tokenService from '../../utils/tokenService';
import { companySubtitle } from './company-subtitle';
import { LogoOrMonogram } from './monogram';
import {
  ChevronDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowRightOnRectangleIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const CompanyChanger = ({ position, onChange }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { list, selected } = useSelector((state) => state.companyReducer);
  const { roles, user } = useSelector((state) => state.authReducer);
  const isAdmin = roles.includes(100);
  const isStoreOwner = roles.includes(150);

  const [form, setForm] = useState({
    id: '',
    logo: '',
    emaildomain: '',
    name: '',
    users: [],
    isactive: true,
    ownername: '',
    ownerphone: '',
    owneremail: '',
    subscriptionid: '',
    creditcardfeeper: null,
    creditcardfeerate: null,
  });

  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    loginAsCompany: false,
  });

  const [errors, setErrors] = useState({
    name: false,
    ownername: false,
    ownerphone: false,
  });

  const [dialogs, setDialogs] = useState({
    storeList: false,
    userList: false,
    upsert: false,
  });

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const getList = async () => {
    try {
      const list = await companyApi.list();
      if (list.data.data.length > 0 && !roles.includes(100)) {
        dispatch(setCompany(list.data.data));
      } else {
        dispatch(setCompany([]));
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onClickAddCompany = () => {
    setForm({
      id: '',
      logo: '',
      emaildomain: '',
      name: '',
      ownername: selected.ownername,
      ownerphone: selected.ownerphone,
      owneremail: selected.owneremail,
      isactive: true,
      users: [user.id],
      sendreport: true,
    });

    setDialogs((prev) => ({ ...prev, upsert: true }));
  };
  const onFormSubmit = async () => {
    if (!form.name || !form.ownername || !form.ownerphone) {
      setErrors((prev) => ({
        ...prev,
        name: !form.name ? true : false,
        ownername: !form.ownername ? true : false,
        ownerphone: !form.ownerphone ? true : false,
      }));
      return;
    } else {
      setErrors((prev) => ({ ...prev, name: false }));
    }
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...form };
      const formData = new FormData();

      Object.keys(body).forEach((key) => {
        if (body[key]) {
          formData.append(key, body[key]);
        }
      });

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };
      const res = await companyApi.upsert(formData, config);
      if (res.data.data) {
        setDialogs((prev) => ({ ...prev, upsert: false }));
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getList();
    }
  };
  const onCloseUpsert = () => {
    setErrors((prev) => ({
      ...prev,
      name: false,
      ownername: false,
      ownerphone: false,
    }));
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onSignout = () => {
    tokenService.removeToken();
    tokenService.removeComp();
    tokenService.removeRole();
    tokenService.removeRolePermissions();
    dispatch(loginFailure());
  };

  const uploadButton = (
    <div>
      {loading.upsertLoading ? <LoadingOutlined /> : <PlusOutlined />}
      <div
        className="dark:text-slate-200"
        style={{
          marginTop: 8,
        }}
      >
        Upload
      </div>
    </div>
  );

  const handleCompanyChange = (company) => {
    dispatch(setGlobalCompany(company));
    navigate(0);
  };

  const [query, setQuery] = useState('');
  const filteredList = (list || []).filter((c) =>
    (c.name || '').toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <>
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button
              className={classNames(
                'group inline-flex items-center gap-2 rounded-md border bg-white px-2 py-1.5 pr-3 text-sm font-semibold leading-none transition focus:outline-none dark:bg-slate-800',
                open
                  ? 'border-blue-500 ring-2 ring-blue-500/15 dark:border-sky-400 dark:ring-sky-400/20'
                  : 'border-slate-200 hover:border-slate-300 hover:shadow-sm dark:border-slate-700 dark:hover:border-slate-600',
              )}
            >
              {isAdmin ? (
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-sm dark:from-sky-400 dark:to-indigo-500">
                  <BuildingOffice2Icon className="h-4 w-4" />
                </span>
              ) : (
                <LogoOrMonogram
                  src={selected.logo}
                  name={selected.name}
                  alt={selected.name}
                  size={32}
                  imgClassName="flex-none rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-600"
                />
              )}
              <span className="hidden flex-col items-start lg:flex">
                <span className="text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                  {isAdmin ? 'Console' : 'Company'}
                </span>
                <span className="mt-0.5 max-w-[10rem] truncate text-sm font-bold text-slate-800 dark:text-slate-100">
                  {isAdmin ? 'Admin' : selected.name || '—'}
                </span>
              </span>
              <ChevronDownIcon
                className={classNames(
                  'h-4 w-4 text-slate-400 transition group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300',
                  open && 'rotate-180',
                )}
                aria-hidden="true"
              />
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 -translate-y-1 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 -translate-y-1 scale-95"
            >
              <Popover.Panel className="absolute right-0 z-20 mt-3 w-[22rem] origin-top-right overflow-hidden rounded-md border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 ring-1 ring-slate-900/5 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:shadow-black/40 dark:ring-white/5">
                {({ close }) => (
                  <div className="flex flex-col">
                    {!isAdmin && (
                      <div className="border-b border-slate-100 px-4 pb-3 pt-4 dark:border-slate-700/70">
                        <p className="text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                          Switch Company
                        </p>
                        {(list || []).length > 5 && (
                          <div className="mt-3 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/15 dark:border-slate-600 dark:bg-slate-700/60 dark:focus-within:border-sky-400 dark:focus-within:ring-sky-400/20">
                            <MagnifyingGlassIcon className="h-3.5 w-3.5 text-slate-400" />
                            <input
                              type="text"
                              value={query}
                              onChange={(e) => setQuery(e.target.value)}
                              placeholder="Search company"
                              className="w-full bg-transparent text-xs font-medium text-slate-700 placeholder-slate-400 focus:outline-none dark:text-slate-100"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {!isAdmin && (
                      <div className="max-h-[18rem] overflow-y-auto p-2 hide-scrollbar">
                        {filteredList.length === 0 ? (
                          <div className="px-3 py-6 text-center text-xs text-slate-400">
                            No companies match.
                          </div>
                        ) : (
                          filteredList.map((item) => {
                            const isSelected = selected.id === item.id;
                            return (
                              <button
                                type="button"
                                key={item.id || item.name}
                                onClick={() => handleCompanyChange(item)}
                                className={classNames(
                                  'group relative flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left transition',
                                  isSelected
                                    ? 'bg-blue-500/10 dark:bg-sky-400/10'
                                    : 'hover:bg-slate-100 dark:hover:bg-slate-700/60',
                                )}
                              >
                                <LogoOrMonogram
                                  src={item.logo}
                                  name={item.name}
                                  size={36}
                                  imgClassName="flex-none rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-600"
                                />
                                <div className="min-w-0 flex-1">
                                  <p
                                    className={classNames(
                                      'truncate text-sm font-semibold',
                                      isSelected
                                        ? 'text-blue-700 dark:text-sky-300'
                                        : 'text-slate-800 dark:text-slate-100',
                                    )}
                                  >
                                    {item.name}
                                  </p>
                                  {companySubtitle(item) ? (
                                    <p className="truncate text-[0.7rem] text-slate-400 dark:text-slate-500">
                                      {companySubtitle(item)}
                                    </p>
                                  ) : null}
                                </div>
                                {isSelected ? (
                                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-white shadow-sm dark:bg-sky-400 dark:text-slate-900">
                                    <CheckIcon className="h-3.5 w-3.5" />
                                  </span>
                                ) : (
                                  <span className="h-6 w-6" />
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}

                    <div
                      className={classNames(
                        'grid divide-slate-100 border-t border-slate-100 bg-slate-50/70 dark:divide-slate-700/70 dark:border-slate-700/70 dark:bg-slate-900/40',
                        !isAdmin && isStoreOwner
                          ? 'grid-cols-2 divide-x'
                          : 'grid-cols-1',
                      )}
                    >
                      {!isAdmin && isStoreOwner && (
                        <button
                          type="button"
                          onClick={() => {
                            close();
                            onClickAddCompany();
                          }}
                          className="group inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-blue-500/5 hover:text-blue-700 dark:text-slate-200 dark:hover:bg-sky-400/10 dark:hover:text-sky-300"
                        >
                          <PlusIcon className="h-4 w-4 transition group-hover:rotate-90" />
                          Add Company
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onSignout()}
                        className="group inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-rose-500/5 hover:text-rose-600 dark:text-slate-200 dark:hover:bg-rose-500/10 dark:hover:text-rose-300"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
      {/* <Menu as="div" className="relative ml-3">
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-10 mt-2 min-w-max origin-top-right rounded-md bg-white dark:bg-slate-900 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {!roles.includes(100) && (
              <div className="relative">
                {list.map((item, itemIdx) => (
                  <Menu.Item key={itemIdx}>
                    {({ active }) => (
                      <a
                        onClick={() => {
                          dispatch(setGlobalCompany(item));
                        }}
                        className={classNames(
                          active ? "bg-gray-100" : "",
                          "relative pl-4 py-2 text-sm text-gray-700 flex items-center space-x-2 pr-9 cursor-pointer dark:hover:bg-slate-700"
                        )}
                      >
                        {item.logo ? (
                          <img
                            className="h-8 w-8 rounded-full"
                            src={item.logo}
                            alt=""
                          />
                        ) : (
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-sky-300">
                            <span className="text-xs font-medium leading-none text-white ">
                              {item.name?.split(" ").map((x) => x[0])}
                            </span>
                          </span>
                        )}
                        <span className="pr-2 dark:text-slate-400">
                          {item.name}
                        </span>
                        {selected.id === item.id && (
                          <span
                            className={classNames(
                              active ? "text-blue-600" : "text-blue-600",
                              "absolute inset-y-0 right-0 flex items-center pr-4"
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </a>
                    )}
                  </Menu.Item>
                ))}

                {!roles.some((role) => [200, 250].includes(role)) && (
                  <Menu.Item>
                    {({ active }) => (
                      <a
                        onClick={() => onClickAddCompany()}
                        className={classNames(
                          active ? "bg-gray-100" : "",
                          " pl-4 pr-9 py-2 text-sm text-gray-700 flex items-center space-x-2 cursor-pointer dark:text-slate-400 dark:hover:bg-slate-700",
                          "after:absolute after:w-[90%] after:bg-gray-200 after:bottom-0 after:left-[5%] after:h-[2px]"
                        )}
                      >
                        <div
                          className={classNames(
                            active ? "bg-gray-100" : "bg-white",
                            "h-8 w-8 rounded-full "
                          )}
                        ></div>

                        <span className="">+ Add Company</span>
                      </a>
                    )}
                  </Menu.Item>
                )}
              </div>
            )}
            <Menu.Item>
              {({ active }) => (
                <a
                  onClick={() => onSignout()}
                  className={classNames(
                    active ? "bg-gray-100" : "",
                    "block px-4 py-2 text-sm text-gray-700 cursor-pointer dark:text-slate-400 dark:hover:bg-slate-700"
                  )}
                >
                  Sign Out
                </a>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu> */}
      <TWModal
        open={dialogs.upsert}
        onClose={() => {}}
        dismissible={false}
        size="2xl"
        panelClassName="dark:text-slate-200"
      >
        <div className="space-y-5">
          <div>
            <label
              htmlFor="logo"
              className="block pb-3 text-sm font-medium text-gray-700 dark:text-slate-200"
            >
              Company Logo
            </label>
            <div className="mt-1">
              <>
                <Upload
                  multiple={false}
                  maxCount={1}
                  name="avatar"
                  onChange={(e) => onFormChange(e.file, 'logo')}
                  listType="picture-circle"
                  className="avatar-uploader"
                  beforeUpload={() => false}
                >
                  {form.imageid ? (
                    <img
                      src={form.logo}
                      alt="avatar"
                      className="min-h-[4rem] min-w-[4rem] h-16 w-16 rounded-full"
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  ) : (
                    uploadButton
                  )}
                </Upload>
              </>
              <div className="h-2 mt-1  text-xs"></div>
            </div>
          </div>
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Company Name
            </label>
            <div className="mt-1">
              <input
                value={form.name}
                onChange={(e) => onFormChange(e.target.value, 'name')}
                type="text"
                name="name"
                id="name"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="e.g. 'Abc Pizza'"
              />
              <div className="h-2 mt-1  text-xs">
                {errors.name && (
                  <p className="text-red-700">Company name can not be empty</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex space-x-4">
            <div className="w-1/2">
              <label
                htmlFor="ownername"
                className="block text-sm font-medium text-gray-700 dark:text-slate-400"
              >
                Owner Name
              </label>
              <div className="mt-1">
                <input
                  value={form.ownername}
                  onChange={(e) => onFormChange(e.target.value, 'ownername')}
                  type="text"
                  name="ownername"
                  id="ownername"
                  className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                  placeholder="e.g. 'John Doe'"
                />
                <div className="h-2 mt-1  text-xs">
                  {errors.ownername && (
                    <p className="text-red-700">Owner name can not be empty</p>
                  )}
                </div>
              </div>
            </div>
            <div className="w-1/2">
              <label
                htmlFor="ownerphone"
                className="block text-sm font-medium text-gray-700 dark:text-slate-400"
              >
                Owner Phone
              </label>
              <div className="mt-1">
                <ReactInputMask
                  mask="(999) 999 9999"
                  maskPlaceholder={' '}
                  value={form.ownerphone}
                  onChange={(e) => onFormChange(e.target.value, 'ownerphone')}
                  type="text"
                  name="ownerphone"
                  id="ownerphone"
                  className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                />
                <div className="h-2 mt-1  text-xs">
                  {errors.ownerphone && (
                    <p className="text-red-700">Owner phone can not be empty</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div>
            <label
              htmlFor="owneremail"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Owner E-mail
            </label>
            <div className="mt-1">
              <input
                value={form.owneremail}
                onChange={(e) => onFormChange(e.target.value, 'owneremail')}
                type="text"
                name="owneremail"
                id="owneremail"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="e.g. 'owner@abc.com'"
              />
              <div className="h-2 mt-1  text-xs"></div>
            </div>
          </div>
          <div className="h-2 mt-1  text-xs"></div>
          <div className="mt-5 pt-2 sm:mt-6 sm:grid z-10 sm:grid-flow-row-dense sm:grid-cols-12 sm:gap-3">
            <TWButton
              variant="default"
              label="Cancel"
              disabled={loading.upsertLoading}
              onClick={() => onCloseUpsert()}
              className="col-span-6 mt-3 w-full sm:mt-0 sm:mb-0"
            />
            <TWButton
              variant="primary"
              label="Add Company"
              disabled={loading.upsertLoading}
              loading={loading.upsertLoading}
              onClick={() => onFormSubmit()}
              className="col-span-6 w-full"
            />
          </div>
        </div>
      </TWModal>
    </>
  );
};

export default CompanyChanger;
